// src/components/appSettings/AppSettingsDashboard.jsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiSettings,
  FiImage,
  FiPackage,
  FiMapPin,
  FiToggleRight,
  FiSliders,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiArrowRight,
} from "react-icons/fi";
import {
  fetchAllBannersThunk,
  fetchAllCollectionsThunk,
  fetchAllContactInfoThunk,
} from "../../store/thunks/appSettingsThunks";
import Button from "../../components/common/Button";

const AppSettingsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { banners, collections, contactInfos, featureFlags, loadingList } =
    useSelector((state) => state.appSettings);

  useEffect(() => {
    // Load all app settings data
    dispatch(fetchAllBannersThunk());
    dispatch(fetchAllCollectionsThunk());
    dispatch(fetchAllContactInfoThunk());
  }, [dispatch]);

  // Calculate stats
  const stats = {
    banners: {
      total: banners?.length || 0,
      active: banners?.filter((b) => b.isActive).length || 0,
      scheduled:
        banners?.filter((b) => {
          const now = new Date();
          return b.isActive && b.validFrom && new Date(b.validFrom) > now;
        }).length || 0,
    },
    collections: {
      total: collections?.length || 0,
      active: collections?.filter((c) => c.isActive).length || 0,
      featured: collections?.filter((c) => c.isFeatured).length || 0,
    },
    // contacts: {
    //   total: contactInfos?.length || 0,
    //   active: contactInfos?.filter((c) => c.isActive).length || 0,
    //   mainBranch: contactInfos?.filter((c) => c.isMainBranch).length || 0,
    // },
    featureFlags: {
      total: featureFlags?.length || 0,
      enabled:
        featureFlags?.filter((f) => f.isEnabled === true || f.isEnabled > 0)
          .length || 0,
      temporary: featureFlags?.filter((f) => f.isTemporary).length || 0,
    },
  };

  // Get recent activity
  const recentBanners = banners?.slice(0, 5) || [];
  const recentCollections = collections?.slice(0, 5) || [];

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          App Settings Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your application settings and content
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4 mb-6">
        <QuickActionCard
          icon={FiImage}
          title="Add Banner"
          color="blue"
          onClick={() => navigate("/app-settings/banners/new")}
        />
        <QuickActionCard
          icon={FiPackage}
          title="New Collection"
          color="purple"
          onClick={() => navigate("/app-settings/collections/new")}
        />
        {/* <QuickActionCard
          icon={FiMapPin}
          title="Add Location"
          color="red"
          onClick={() => navigate("/app-settings/contact/new")}
        /> */}
        {/* <QuickActionCard
          icon={FiToggleRight}
          title="Feature Flags"
          color="orange"
          onClick={() => navigate("/app-settings/feature-flags")}
        /> */}
        {/* <QuickActionCard
          icon={FiSliders}
          title="App Config"
          color="indigo"
          onClick={() => navigate("/app-settings/config")}
        /> */}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-6 mb-6">
        <StatsCard
          title="Banners"
          icon={FiImage}
          total={stats.banners.total}
          active={stats.banners.active}
          additional={`${stats.banners.scheduled} scheduled`}
          color="blue"
          onClick={() => navigate("/app-settings/banners")}
        />
        <StatsCard
          title="Collections"
          icon={FiPackage}
          total={stats.collections.total}
          active={stats.collections.active}
          additional={`${stats.collections.featured} featured`}
          color="purple"
          onClick={() => navigate("/app-settings/collections")}
        />
        {/* <StatsCard
          title="Locations"
          icon={FiMapPin}
          total={stats.contacts.total}
          active={stats.contacts.active}
          additional={`${stats.contacts.mainBranch} main`}
          color="red"
          onClick={() => navigate("/app-settings/contact")}
        /> */}
        {/* <StatsCard
          title="Feature Flags"
          icon={FiToggleRight}
          total={stats.featureFlags.total}
          active={stats.featureFlags.enabled}
          additional={`${stats.featureFlags.temporary} temporary`}
          color="orange"
          onClick={() => navigate("/app-settings/feature-flags")}
        /> */}
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Banners */}
        <ActivityCard
          title="Recent Banners"
          icon={FiImage}
          items={recentBanners}
          emptyMessage="No banners yet"
          onViewAll={() => navigate("/app-settings/banners")}
          renderItem={(banner) => (
            <ActivityItem
              key={banner._id}
              title={banner.title}
              subtitle={banner.placement}
              status={banner.isActive ? "active" : "inactive"}
              onClick={() => navigate(`/app-settings/banners/${banner._id}`)}
            />
          )}
        />

        {/* Recent Collections */}
        <ActivityCard
          title="Recent Collections"
          icon={FiPackage}
          items={recentCollections}
          emptyMessage="No collections yet"
          onViewAll={() => navigate("/app-settings/collections")}
          renderItem={(collection) => (
            <ActivityItem
              key={collection._id}
              title={collection.name}
              subtitle={collection.slug}
              status={collection.isActive ? "active" : "inactive"}
              badge={collection.isFeatured ? "Featured" : null}
              onClick={() =>
                navigate(`/app-settings/collections/${collection._id}`)
              }
            />
          )}
        />
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health Check */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">System Health</h3>
            <FiCheckCircle className="text-green-600 text-xl" />
          </div>

          <div className="space-y-3">
            <HealthItem
              label="Banners Service"
              status="operational"
              message="All banners loading correctly"
            />
            <HealthItem
              label="Collections Service"
              status="operational"
              message="Product filtering active"
            />
            <HealthItem
              label="Contact Service"
              status="operational"
              message="Location data accessible"
            />
            <HealthItem
              label="Feature Flags"
              status="operational"
              message="All flags responsive"
            />
          </div>
        </div>

        {/* Alerts & Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Alerts & Notifications
            </h3>
            <FiAlertCircle className="text-orange-600 text-xl" />
          </div>

          <div className="space-y-3">
            {stats.banners.scheduled > 0 && (
              <AlertItem
                type="info"
                message={`${stats.banners.scheduled} banner${
                  stats.banners.scheduled !== 1 ? "s" : ""
                } scheduled to go live`}
                action="View Banners"
                onClick={() => navigate("/app-settings/banners")}
              />
            )}

            {/* {stats.featureFlags.temporary > 0 && (
              <AlertItem
                type="info"
                message={`${
                  stats.featureFlags.temporary
                } temporary feature flag${
                  stats.featureFlags.temporary !== 1 ? "s" : ""
                } active`}
                action="Manage Flags"
                onClick={() => navigate("/app-settings/feature-flags")}
              />
            )} */}

            {/* {stats.contacts.total === 0 && (
              <AlertItem
                type="warning"
                message="No store locations configured"
                action="Add Location"
                onClick={() => navigate("/app-settings/contact/new")}
              />
            )} */}

            {stats.collections.total === 0 && (
              <AlertItem
                type="warning"
                message="No product collections created"
                action="Create Collection"
                onClick={() => navigate("/app-settings/collections/new")}
              />
            )}

            {stats.banners.total === 0 &&
              stats.collections.total === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">
                    No alerts at this time
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    System running smoothly
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ icon: Icon, title, color, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-lg shadow p-4 hover:shadow-md transition-all duration-200 border-2 border-transparent hover:border-${color}-200`}
  >
    <div
      className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center mb-3 mx-auto`}
    >
      <Icon className={`text-${color}-600 text-xl`} />
    </div>
    <p className="text-sm font-medium text-gray-900 text-center">{title}</p>
  </button>
);

// Stats Card Component
const StatsCard = ({
  title,
  icon: Icon,
  total,
  active,
  additional,
  color,
  onClick,
}) => (
  <div
    onClick={onClick}
    className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
  >
    <div className="flex items-center justify-between mb-4">
      <Icon className={`text-2xl text-${color}-600`} />
      <FiArrowRight className="text-gray-400" />
    </div>

    <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>

    <div className="flex items-baseline gap-2 mb-2">
      <p className="text-3xl font-bold text-gray-900">{total}</p>
      <span className="text-sm text-gray-500">total</span>
    </div>

    <div className="flex items-center justify-between text-xs">
      <span className="text-green-600 font-medium">{active} active</span>
      <span className="text-gray-500">{additional}</span>
    </div>
  </div>
);

// Activity Card Component
const ActivityCard = ({
  title,
  icon: Icon,
  items,
  emptyMessage,
  onViewAll,
  renderItem,
}) => (
  <div className="bg-white rounded-lg shadow">
    <div className="p-4 border-b flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="text-gray-600" />
        <h3 className="font-medium text-gray-900">{title}</h3>
      </div>
      <button
        onClick={onViewAll}
        className="text-sm text-primary hover:underline"
      >
        View All
      </button>
    </div>

    <div className="p-4">
      {items.length > 0 ? (
        <div className="space-y-2">{items.map(renderItem)}</div>
      ) : (
        <p className="text-center text-gray-500 text-sm py-8">{emptyMessage}</p>
      )}
    </div>
  </div>
);

// Activity Item Component
const ActivityItem = ({ title, subtitle, status, badge, onClick }) => (
  <div
    onClick={onClick}
    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        {badge && (
          <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 truncate">{subtitle}</p>
    </div>
    <span
      className={`ml-2 px-2 py-1 text-xs rounded-full ${
        status === "active"
          ? "bg-green-100 text-green-800"
          : "bg-gray-100 text-gray-800"
      }`}
    >
      {status}
    </span>
  </div>
);

// Health Item Component
const HealthItem = ({ label, status, message }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-3">
      <div
        className={`w-2 h-2 rounded-full ${
          status === "operational" ? "bg-green-500" : "bg-red-500"
        }`}
      />
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{message}</p>
      </div>
    </div>
    <FiCheckCircle
      className={status === "operational" ? "text-green-600" : "text-gray-400"}
    />
  </div>
);

// Alert Item Component
const AlertItem = ({ type, message, action, onClick }) => {
  const getStyles = () => {
    switch (type) {
      case "warning":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          icon: "text-yellow-600",
          text: "text-yellow-900",
        };
      case "error":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: "text-red-600",
          text: "text-red-900",
        };
      default:
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: "text-blue-600",
          text: "text-blue-900",
        };
    }
  };

  const styles = getStyles();

  return (
    <div
      className={`${styles.bg} border ${styles.border} rounded-lg p-3 flex items-start justify-between`}
    >
      <div className="flex items-start gap-2 flex-1">
        <FiAlertCircle className={`${styles.icon} mt-0.5 flex-shrink-0`} />
        <p className={`text-sm ${styles.text}`}>{message}</p>
      </div>
      {action && (
        <button
          onClick={onClick}
          className={`ml-2 text-xs font-medium ${styles.text} hover:underline whitespace-nowrap`}
        >
          {action}
        </button>
      )}
    </div>
  );
};

export default AppSettingsDashboard;
